import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PUBLIC_RECORD, publicRecordState } from '@/features/publicsite/publicRecord';
import { useLanguage } from '@/i18n/LanguageContext';
import { eventPath } from '@/features/publicsite/eventPath';
import { LANGUAGE_FALLBACK } from '@/features/common/languageFallback';
import { languageFallbackOf, servedLanguage } from '@/features/publicsite/contentLanguage';
import { publicEventsApi } from '@/features/publicsite/events/api/publicEventsApi';
import { usePageLanguageFallback } from '@/features/publicsite/usePageLanguageFallback';
import type { EventDetail } from '@/features/publicsite/types/events/EventDetail';
import type { EventFeedbackSummary } from '@/features/publicsite/types/events/EventFeedbackSummary';

/**
 * One event, by the `:slug` route param (a slug or a bare id), and its aggregate rating.
 *
 * `missing` ends the page in its not-found screen; `refreshFeedback` re-reads the rating after the
 * visitor's own evaluation is accepted, so the average they see includes it.
 */
export function useEventDetail(param: string) {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [event, setEvent] = useState<EventDetail | null>(null);
  const [feedback, setFeedback] = useState<EventFeedbackSummary | null>(null);
  const [missing, setMissing] = useState(false);

  // Set right before a canonicalizing redirect (below) and consumed by the next run of this
  // effect. Without it, navigate() changing the :slug param — which this effect depends on —
  // re-triggered the same fetch for data just loaded: wasted requests always, and on a bare-id
  // link, a transient failure on that redundant second fetch replaced an already-working page
  // with "not found".
  const skipNextFetchRef = useRef(false);

  useEffect(() => {
    let live = true;
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
      return;
    }
    setMissing(false);
    publicEventsApi
      .detail(param)
      .then((e) => {
        if (!live) return;
        setEvent(e);
        // A visitor who arrived on a bare id — an old link, a bookmark, a hand-typed URL — gets
        // the readable address once the title is known. `replace` so the back button still goes
        // back to the calendar rather than to the same page under its other name.
        const canonical = eventPath(e);
        if (canonical !== `/evenimente/${param}`) {
          skipNextFetchRef.current = true;
          navigate(canonical, { replace: true });
        }
      })
      .catch((err) => {
        if (!live) return;
        // Any failure ends in the not-found screen, not only a 404 — rethrowing left the page on
        // "Se încarcă…" for good: no error, no content, a spinner with nothing behind it. Same
        // reasoning ContentPage already applies to its own loads: whatever went wrong, there is
        // nothing here to read, and saying so is the honest end state.
        if (publicRecordState(err) !== PUBLIC_RECORD.missing) {
          console.error('Failed to load event', err);
        }
        setMissing(true);
      });
    publicEventsApi
      .feedbackSummary(param)
      .then((f) => {
        if (live) setFeedback(f);
      })
      .catch(() => {
        /* a missing summary is not a reason to blank the page */
      });
    return () => {
      live = false;
    };
  }, [param, language, navigate]);

  // Derived from `contentLanguage` rather than read from `languageFallback`, so the notice is right
  // against a server that sends neither: an event it does not translate is Romanian.
  usePageLanguageFallback(
    !missing && event
      ? languageFallbackOf([servedLanguage(event)], language)
      : LANGUAGE_FALLBACK.none,
  );

  const refreshFeedback = () => {
    publicEventsApi
      .feedbackSummary(param)
      .then(setFeedback)
      .catch(() => undefined);
  };

  return { event, feedback, missing, refreshFeedback };
}
